import { normalizeGroups } from "./canvas-groups";
import type { Edge, Node } from "@xyflow/react";
import { boardSnapshotSchema } from "../validation/board-snapshot";
import { diagramRevisionSchema } from "../validation/diagram-revision";
import type { BoardDocument, BoardSnapshot, DiagramCheckpoint } from "../types/board";

export function decodeBoardSnapshot(raw: unknown): BoardSnapshot {
  const parsed = boardSnapshotSchema.parse(raw === null ? { nodes: [] }
    : typeof raw === "string" ? JSON.parse(raw) : raw);
  return normalizeGroups(structuredClone({ ...parsed, format: "reactflow", version: 1,
    nodes: parsed.nodes.map(node => ({ ...node, data: node.data ?? {} })) as Node[],
    edges: (parsed.edges ?? []) as Edge[],
  }) as BoardSnapshot);
}

export function decodeCheckpoint(diagram: { title: string; snapshot: unknown; revision: string }): DiagramCheckpoint {
  if (typeof diagram.title !== "string") throw new Error("Invalid diagram title");
  return { revision: diagramRevisionSchema.parse(diagram.revision),
    document: { title: diagram.title, snapshot: decodeBoardSnapshot(diagram.snapshot) } };
}

export function persistableSnapshot(snapshot: BoardSnapshot): BoardSnapshot {
  const clean = structuredClone(snapshot);
  for (const node of clean.nodes) {
    delete node.selected; delete node.dragging; delete node.resizing; delete node.measured;
  }
  for (const edge of clean.edges) delete edge.selected;
  return clean;
}

function ordered(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(ordered);
  if (value !== null && typeof value === "object")
    return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)
      .map(([key, item]) => [key, ordered(item)]));
  return value;
}

export function canonicalDocument(document: BoardDocument): string {
  const snapshot = persistableSnapshot(document.snapshot);
  delete snapshot.viewport;
  return JSON.stringify(ordered({ title: document.title, snapshot }));
}

export function recoveryCopy(diagramId: string, baseRevision: string | null, document: BoardDocument) {
  return { diagram_id: diagramId, base_revision: baseRevision, title: document.title,
    snapshot: persistableSnapshot(document.snapshot), exported_at: new Date().toISOString() };
}

export function downloadRecovery(diagramId: string, value: unknown) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }));
  try {
    const link = document.createElement("a");
    link.href = url; link.download = `${diagramId}-recovery.json`;
    document.body.appendChild(link);
    try { link.click(); } finally { link.remove(); }
  } finally { URL.revokeObjectURL(url); }
}
